import React from 'react';
import { HomeIcon, ServerIcon, FolderIcon } from '@heroicons/react/24/solid';

// Mock active HAProxy data
const haproxyInstances = [
  { id: 'h1', name: 'HAProxy-Prod', status: 'healthy', lastChecked: '5m ago', requests: '1.2M' },
  { id: 'h2', name: 'HAProxy-Staging', status: 'healthy', lastChecked: '12m ago', requests: '320K' },
];
const haproxyBackends = [
  { id: 'b1', name: 'api-prod', servers: 3, status: 'healthy', requests: '900K' },
  { id: 'b2', name: 'web-frontend', servers: 2, status: 'healthy', requests: '600K' },
];
const haproxyDomains = [
  { id: 'd1', domain: 'example.com', backend: 'api-prod', status: 'active' },
  { id: 'd2', domain: 'mysite.com', backend: 'web-frontend', status: 'active' },
];

// Mock active NFS data
const nfsServers = [
  { id: 'n1', name: 'NFS-Server-1', status: 'running', lastChecked: '10m ago', used: '1.1TB/2TB' },
  { id: 'n2', name: 'NFS-Server-2', status: 'running', lastChecked: '20m ago', used: '800GB/1TB' },
];
const nfsShares = [
  { id: 's1', path: '/data', server: 'NFS-Server-1', access: 'rw', used: '900GB' },
  { id: 's2', path: '/media', server: 'NFS-Server-2', access: 'ro', used: '600GB' },
];

const statusDot = (status: string) => {
  if (status === 'healthy' || status === 'active' || status === 'running') return <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-2" />;
  if (status === 'degraded') return <span className="inline-block h-2 w-2 rounded-full bg-yellow-400 mr-2" />;
  return <span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-2" />;
};
const accessBadge = (access: string) => (
  <span className={`ml-2 inline-flex items-center rounded-full ${access === 'rw' ? 'bg-blue-600/20 text-blue-400' : 'bg-zinc-600/20 text-zinc-300'} px-2 py-0.5 text-xs font-medium`}>{access.toUpperCase()}</span>
);

const Dashboard: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
    {/* Header */}
    <div className="mb-8">
      <div className="relative shadow-sm">
        <div className="mx-auto px-4 sm:px-6 lg:px-0">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded bg-indigo-500 flex items-center justify-center">
                  <HomeIcon className="h-5 w-5 text-white" aria-hidden="true" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-zinc-100">Dashboard</h1>
                <p className="text-xs text-zinc-400">Active Infrastructure Overview</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
      {/* Active HAProxy */}
      <div className="overflow-hidden rounded-2xl bg-zinc-800 shadow p-6 flex flex-col">
        <div className="flex items-center mb-4">
          <ServerIcon className="h-6 w-6 text-indigo-400 mr-2" />
          <h2 className="text-lg font-semibold text-zinc-100">Active HAProxy</h2>
          <span className="ml-3 text-xs text-zinc-400">{haproxyInstances.length} Instances, {haproxyBackends.length} Backends, {haproxyDomains.length} Domains</span>
        </div>
        {/* Instances */}
        <div className="mb-6">
          <div className="text-xs text-zinc-400 mb-2 font-semibold uppercase tracking-wide">Instances</div>
          <ul className="divide-y divide-zinc-700">
            {haproxyInstances.map(i => (
              <li key={i.id} className="py-2 flex items-center">
                {statusDot(i.status)}
                <span className="text-sm text-zinc-100 font-medium">{i.name}</span>
                <span className="ml-4 text-xs text-zinc-400" title="Requests handled">{i.requests} req</span>
                <span className="ml-auto text-xs text-zinc-400">{i.lastChecked}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Backends */}
        <div className="mb-6">
          <div className="text-xs text-zinc-400 mb-2 font-semibold uppercase tracking-wide">Backends</div>
          <ul className="divide-y divide-zinc-700">
            {haproxyBackends.map(b => (
              <li key={b.id} className="py-2 flex items-center">
                {statusDot(b.status)}
                <span className="text-sm text-zinc-100 font-medium">{b.name}</span>
                <span className="ml-4 text-xs text-zinc-400">{b.servers} servers</span>
                <span className="ml-4 text-xs text-zinc-400" title="Requests handled">{b.requests} req</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Domain Mappings */}
        <div>
          <div className="text-xs text-zinc-400 mb-2 font-semibold uppercase tracking-wide">Domain Mappings</div>
          <ul className="divide-y divide-zinc-700">
            {haproxyDomains.map(d => (
              <li key={d.id} className="py-2 flex items-center">
                {statusDot(d.status)}
                <span className="text-sm text-zinc-100 font-medium">{d.domain}</span>
                <span className="ml-2 text-xs text-zinc-400">→ {d.backend}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Active NFS */}
      <div className="overflow-hidden rounded-2xl bg-zinc-800 shadow p-6 flex flex-col">
        <div className="flex items-center mb-4">
          <FolderIcon className="h-6 w-6 text-emerald-400 mr-2" />
          <h2 className="text-lg font-semibold text-zinc-100">Active NFS</h2>
          <span className="ml-3 text-xs text-zinc-400">{nfsServers.length} Servers, {nfsShares.length} Shares</span>
        </div>
        {/* Servers */}
        <div className="mb-6">
          <div className="text-xs text-zinc-400 mb-2 font-semibold uppercase tracking-wide">Servers</div>
          <ul className="divide-y divide-zinc-700">
            {nfsServers.map(s => (
              <li key={s.id} className="py-2 flex items-center">
                {statusDot(s.status)}
                <span className="text-sm text-zinc-100 font-medium">{s.name}</span>
                <span className="ml-4 text-xs text-zinc-400" title="Storage used">{s.used}</span>
                <span className="ml-auto text-xs text-zinc-400">{s.lastChecked}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Shares */}
        <div>
          <div className="text-xs text-zinc-400 mb-2 font-semibold uppercase tracking-wide">Shares</div>
          <ul className="divide-y divide-zinc-700">
            {nfsShares.map(share => (
              <li key={share.id} className="py-2 flex items-center">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 mr-2" />
                <span className="text-sm text-zinc-100 font-medium">{share.path}</span>
                <span className="ml-2 text-xs text-zinc-400">on {share.server}</span>
                {accessBadge(share.access)}
                <span className="ml-4 text-xs text-zinc-400" title="Storage used">{share.used}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

export default Dashboard; 